import styles from './spacing.module.css';

import type { SpacingCSSProperty, SpacingProps } from './SpacingProps';

/**
 * Return the CSS classes to render a spacing (margin or padding) around an
 * element. To be used by components that extend {@link SpacingProps}.
 *
 * ```tsx
 * type MyComponentProps = SpacingProps & { ... };
 * function MyComponentWithPadding(props: MyComponentProps) {
 *   const spacingClassNames = getSpacingStyles('padding', props);
 *   return <div className={spacingClassNames}>{props.children}</div>;
 * }
 *
 * <MyComponentWithPadding spacing="md" /> // Will render a div with the class for "medium" padding.
 * ```
 *
 * @param property - The CSS property to use for the spacing. Use `margin` or
 *   `padding` depending on the layout you want to achieve.
 * @param props - The spacing props coming from a component extending
 *   {@link SpacingProps}.
 * @internal
 */
export function getSpacingStyles(
  property: SpacingCSSProperty,
  props: SpacingProps
): string[] {
  let { topSpacing, bottomSpacing, leftSpacing, rightSpacing, spacing } = props;

  if (props.spacing === undefined && props.vSpacing !== undefined) {
    // props.vSpacing overrides both top and bottom spacing
    topSpacing = props.vSpacing;
    bottomSpacing = props.vSpacing;
  }
  if (props.spacing === undefined && props.hSpacing !== undefined) {
    // props.hSpacing spacing overrides both left and right spacing
    leftSpacing = props.hSpacing;
    rightSpacing = props.hSpacing;
  }

  const spacingStyles: string[] = [];

  if (typeof spacing === 'string') {
    spacing = { xs: spacing };
  }

  if (typeof spacing === 'number') {
    spacing = { xs: spacing };
  }

  if (spacing?.xs !== undefined) {
    spacingStyles.push(styles[`xs-${property}-${spacing.xs}`]);
  }
  if (spacing?.sm !== undefined) {
    spacingStyles.push(styles[`sm-${property}-${spacing.sm}`]);
  }
  if (spacing?.md !== undefined) {
    spacingStyles.push(styles[`md-${property}-${spacing.md}`]);
  }
  if (spacing?.lg !== undefined) {
    spacingStyles.push(styles[`lg-${property}-${spacing.lg}`]);
  }
  if (spacing?.xl !== undefined) {
    spacingStyles.push(styles[`xl-${property}-${spacing.xl}`]);
  }

  if (typeof topSpacing === 'string') {
    topSpacing = { xs: topSpacing };
  }

  if (typeof topSpacing === 'number') {
    topSpacing = { xs: topSpacing };
  }

  if (topSpacing?.xs !== undefined) {
    spacingStyles.push(styles[`xs-top-${property}-${topSpacing.xs}`]);
  }
  if (topSpacing?.sm !== undefined) {
    spacingStyles.push(styles[`sm-top-${property}-${topSpacing.sm}`]);
  }
  if (topSpacing?.md !== undefined) {
    spacingStyles.push(styles[`md-top-${property}-${topSpacing.md}`]);
  }
  if (topSpacing?.lg !== undefined) {
    spacingStyles.push(styles[`lg-top-${property}-${topSpacing.lg}`]);
  }
  if (topSpacing?.xl !== undefined) {
    spacingStyles.push(styles[`xl-top-${property}-${topSpacing.xl}`]);
  }

  if (typeof bottomSpacing === 'string') {
    bottomSpacing = { xs: bottomSpacing };
  }

  if (typeof bottomSpacing === 'number') {
    bottomSpacing = { xs: bottomSpacing };
  }

  if (bottomSpacing?.xs !== undefined) {
    spacingStyles.push(styles[`xs-bottom-${property}-${bottomSpacing.xs}`]);
  }
  if (bottomSpacing?.sm !== undefined) {
    spacingStyles.push(styles[`sm-bottom-${property}-${bottomSpacing.sm}`]);
  }
  if (bottomSpacing?.md !== undefined) {
    spacingStyles.push(styles[`md-bottom-${property}-${bottomSpacing.md}`]);
  }
  if (bottomSpacing?.lg !== undefined) {
    spacingStyles.push(styles[`lg-bottom-${property}-${bottomSpacing.lg}`]);
  }
  if (bottomSpacing?.xl !== undefined) {
    spacingStyles.push(styles[`xl-bottom-${property}-${bottomSpacing.xl}`]);
  }

  if (typeof rightSpacing === 'string') {
    rightSpacing = { xs: rightSpacing };
  }

  if (typeof rightSpacing === 'number') {
    rightSpacing = { xs: rightSpacing };
  }

  if (rightSpacing?.xs !== undefined) {
    spacingStyles.push(styles[`xs-right-${property}-${rightSpacing.xs}`]);
  }
  if (rightSpacing?.sm !== undefined) {
    spacingStyles.push(styles[`sm-right-${property}-${rightSpacing.sm}`]);
  }
  if (rightSpacing?.md !== undefined) {
    spacingStyles.push(styles[`md-right-${property}-${rightSpacing.md}`]);
  }
  if (rightSpacing?.lg !== undefined) {
    spacingStyles.push(styles[`lg-right-${property}-${rightSpacing.lg}`]);
  }
  if (rightSpacing?.xl !== undefined) {
    spacingStyles.push(styles[`xl-right-${property}-${rightSpacing.xl}`]);
  }

  if (typeof leftSpacing === 'string') {
    leftSpacing = { xs: leftSpacing };
  }

  if (typeof leftSpacing === 'number') {
    leftSpacing = { xs: leftSpacing };
  }

  if (leftSpacing?.xs !== undefined) {
    spacingStyles.push(styles[`xs-left-${property}-${leftSpacing.xs}`]);
  }
  if (leftSpacing?.sm !== undefined) {
    spacingStyles.push(styles[`sm-left-${property}-${leftSpacing.sm}`]);
  }
  if (leftSpacing?.md !== undefined) {
    spacingStyles.push(styles[`md-left-${property}-${leftSpacing.md}`]);
  }
  if (leftSpacing?.lg !== undefined) {
    spacingStyles.push(styles[`lg-left-${property}-${leftSpacing.lg}`]);
  }
  if (leftSpacing?.xl !== undefined) {
    spacingStyles.push(styles[`xl-left-${property}-${leftSpacing.xl}`]);
  }

  return spacingStyles;
}
